import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
// components
import Layout from '@components/layout';
import SEO from '@components/seo';
import Modal from '@components/modal';
import ModalBody from '@components/modal/modal-body/modal-body';
import { TrustPilot } from 'vitl-component-library';
// context
import { GlobalContext } from '@src/store/global-state';
// hooks
import { useGetUserDetails } from '@src/hooks/useGetUserDetails';
// services
import { getCookie, setCookie } from '@src/services/cookies';
import { COOKIE_EXPIRES_IN } from '@src/constants/cookies';
import { appendStoredTrackingParamsToUrl } from '@src/utils/shopify-tracking-params';
import { getShopifyHost } from '@src/utils/shopify-host-redirect';
// styles
import styles from '../../pages/tracker.module.scss';
import fadeTransition from '@src/styles/fade.module.scss';

// UTM parameters for affiliate/partner tracking
export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

// Offer configuration type
export interface OfferConfig {
  type: string;
  hasOffer: boolean;
  hasTrial: boolean;
  // Pricing
  monthlyPrice: string;
  annualPrice: string;
  discountPercent: string;
  // Subscription details (for non-annual offers)
  subscriptionPeriod?: string; // e.g., '3 months', '12 months'
  // Display
  promoBadgeText?: string;
  ctaButtonText: string;
  appName?: string; // Custom app name for partner offers
  // Partner branding
  partner?: {
    name: string;
    logo?: string; // Path to partner logo
  };
  // Default UTM params for this offer (set if no UTM params in cookies)
  defaultUtmParams?: UtmParams;
  // Optional privacy notice for partner pages (displayed in footer)
  privacyNotice?: string;
}

// Offer configurations
// Monthly price: £9.99/month = £119.88/year
// All annual prices are discounted vs monthly
//
// URL Pattern for TRIAL offers: vxt[trial-duration][price][period][random]
//   - trial-duration: 3d = 3 days, 2w = 2 weeks, 0d = no trial
//   - price: scrambled (~£25 = 52, ~£20 = 02, ~£15 = 51, ~£30 = 03)
//   - period: a = annual, m = monthly
//
// URL Pattern for INTRO offers: vxi[intro-price][billing][duration][rollover-price][rollover-period][random]
//   - billing: u = upfront, p = PAYG
//   - duration: 3m, 6m, 1y
//
export const offerConfigs: Record<string, OfferConfig> = {
  // Standard page - no special offer, just the normal 75% discount vs monthly
  none: {
    type: 'none',
    hasOffer: false,
    hasTrial: true,
    monthlyPrice: '£9.99',
    annualPrice: '£29.99',
    discountPercent: '75%',
    promoBadgeText: undefined,
    ctaButtonText: 'Start 3-day free trial',
  },
  // Trial Offers
  // £24.99/year, 79% off, 3-day trial
  'vxt3d52ak': {
    type: 'vxt3d52ak',
    hasOffer: true,
    hasTrial: true,
    monthlyPrice: '£9.99',
    annualPrice: '£24.99',
    discountPercent: '79%',
    promoBadgeText: 'Special offer: 79% with annual plan',
    ctaButtonText: 'Start 3-day free trial',
  },
  // £19.99/year, 83% off, 3-day trial
  'vxt3d02am': {
    type: 'vxt3d02am',
    hasOffer: true,
    hasTrial: true,
    monthlyPrice: '£9.99',
    annualPrice: '£19.99',
    discountPercent: '83%',
    promoBadgeText: 'Special offer: 83% with annual plan',
    ctaButtonText: 'Start 3-day free trial',
  },
  // £24.99/year, 79% off, no trial
  'vxt0d52ar': {
    type: 'vxt0d52ar',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£24.99',
    discountPercent: '79%',
    promoBadgeText: 'Special offer: 79% with annual plan',
    ctaButtonText: 'Claim offer now',
  },
  // £19.99/year, 83% off, no trial
  'vxt0d02ap': {
    type: 'vxt0d02ap',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£19.99',
    discountPercent: '83%',
    promoBadgeText: 'Special offer: 83% with annual plan',
    ctaButtonText: 'Claim offer now',
  },
  // £14.99/year, 87% off, no trial
  'vxt0d51aw': {
    type: 'vxt0d51aw',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£14.99',
    discountPercent: '87%',
    promoBadgeText: 'Special offer: 87% with annual plan',
    ctaButtonText: 'Claim offer now',
  },
  // £14.99/year, 87% off, 3-day trial
  'vxt3d51aj': {
    type: 'vxt3d51aj',
    hasOffer: true,
    hasTrial: true,
    monthlyPrice: '£9.99',
    annualPrice: '£14.99',
    discountPercent: '87%',
    promoBadgeText: 'Special offer: 87% with annual plan',
    ctaButtonText: 'Start 3-day free trial',
  },
  // £19.99/year, 83% off, 2-week trial
  'vxt2w02aq': {
    type: 'vxt2w02aq',
    hasOffer: true,
    hasTrial: true,
    monthlyPrice: '£9.99',
    annualPrice: '£19.99',
    discountPercent: '83%',
    promoBadgeText: 'Special offer: 83% off with annual plan',
    ctaButtonText: 'Start 14-day free trial',
  },
  // Intro Offers
  // £9.99 upfront for 3 months, rolls to £9.99/month (generic)
  'vxi01u3m01mg': {
    type: 'vxi01u3m01mg',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£9.99', // 3 months for £9.99
    discountPercent: '67%', // vs 3 months at £9.99/month = £29.97
    subscriptionPeriod: '3 months',
    promoBadgeText: 'Special offer: 3 months for £9.99',
    ctaButtonText: 'Claim offer now',
  },
  // Partner-specific Offers
  // Digin - £19.99/year, 83% off, 2-week trial
  'digin': {
    type: 'digin',
    hasOffer: true,
    hasTrial: true,
    monthlyPrice: '£9.99',
    annualPrice: '£19.99',
    discountPercent: '83%',
    promoBadgeText: 'Exclusive: 83% off with annual plan',
    ctaButtonText: 'Start 14-day free trial',
    partner: {
      name: 'Digin',
      logo: '/images/tracker/partners/Digin-logo.webp',
    },
    defaultUtmParams: {
      utm_source: 'digin',
      utm_medium: 'partner',
      utm_campaign: 'tracker_web_payments_digin',
    },
  },
  // SheMed - 3 months for £9.99 with partner branding
  'shemed': {
    type: 'shemed',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£9.99', // 3 months for £9.99
    discountPercent: '67%', // vs 3 months at £9.99/month = £29.97
    subscriptionPeriod: '3 months',
    promoBadgeText: 'Exclusive: 3 months for £9.99',
    ctaButtonText: 'Claim offer now',
    appName: 'Vitl AI Nutrition Tracker',
    partner: {
      name: 'SheMed',
      logo: '/images/tracker/partners/SheMed-logo.png',
    },
    defaultUtmParams: {
      utm_source: 'shemed',
      utm_medium: 'partner',
      utm_campaign: 'tracker_web_payments_shemed',
    },
    privacyNotice: 'By claiming this offer, you agree that Vitl may share your details with SheMed in accordance with our',
  },
  // £9.99 upfront for 2 months, rolls to £9.99/month (generic)
  'vxi01u2m01mz': {
    type: 'vxi01u2m01mz',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£9.99', // 2 months for £9.99
    discountPercent: '50%', // vs 2 months at £9.99/month = £19.98
    subscriptionPeriod: '2 months',
    promoBadgeText: 'Special offer: 2 months for £9.99',
    ctaButtonText: 'Claim offer now',
    appName: 'Vitl AI Nutrition Tracker',
  },
  // Google Ads - 2 months for £9.99
  'GAds-2M-10': {
    type: 'GAds-2M-10',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£9.99', // 2 months for £9.99
    discountPercent: '50%', // vs 2 months at £9.99/month = £19.98
    subscriptionPeriod: '2 months',
    promoBadgeText: 'Special offer: 2 months for £9.99',
    ctaButtonText: 'Claim offer now',
    appName: 'Vitl AI Nutrition Tracker',
    defaultUtmParams: {
      utm_source: 'google',
      utm_medium: 'cpc',
      utm_campaign: 'tracker_web_payments_gads_2m',
    },
  },
  // Meta - 2 months for £9.99
  'Meta-2M-10': {
    type: 'Meta-2M-10',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£9.99', // 2 months for £9.99
    discountPercent: '50%', // vs 2 months at £9.99/month = £19.98
    subscriptionPeriod: '2 months',
    promoBadgeText: 'Special offer: 2 months for £9.99',
    ctaButtonText: 'Claim offer now',
    appName: 'Vitl AI Nutrition Tracker',
    defaultUtmParams: {
      utm_source: 'facebook',
      utm_medium: 'cpc',
      utm_campaign: 'tracker_web_payments_meta_2m',
    },
  },
  // TikTok - 2 months for £9.99
  'TikTok-2M-10': {
    type: 'TikTok-2M-10',
    hasOffer: true,
    hasTrial: false,
    monthlyPrice: '£9.99',
    annualPrice: '£9.99', // 2 months for £9.99
    discountPercent: '50%', // vs 2 months at £9.99/month = £19.98
    subscriptionPeriod: '2 months',
    promoBadgeText: 'Special offer: 2 months for £9.99',
    ctaButtonText: 'Claim offer now',
    appName: 'Vitl AI Nutrition Tracker',
    defaultUtmParams: {
      utm_source: 'tiktok',
      utm_medium: 'cpc',
      utm_campaign: 'tracker_web_payments_tiktok_2m',
    },
  },
};

interface TrackerLandingProps {
  offer?: OfferConfig;
}

const APP_STORE_URL = 'https://apps.apple.com/gb/app/vitl-ai-calorie-tracker/id6744249515';

// Standard UTM parameter names
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

const buildShopifyAccountUrl = (
  path: '/account/login' | '/account/register',
  returnPath: string
) => {
  const shopifyHost = getShopifyHost();
  if (!shopifyHost) {
    return path;
  }
  const siteHost = process.env.GATSBY_HOST_NAME || (typeof window !== 'undefined' ? window.location.origin : '');
  const absoluteReturnUrl = siteHost ? `${siteHost.replace(/\/+$/, '')}${returnPath}` : returnPath;
  const returnUrl = encodeURIComponent(absoluteReturnUrl);

  return appendStoredTrackingParamsToUrl(`${shopifyHost}${path}?return_url=${returnUrl}`);
};

const TrackerLanding: React.FC<TrackerLandingProps> = ({ offer = offerConfigs.none }) => {
  const { isLoggedIn } = useContext(GlobalContext);
  const { user } = useGetUserDetails();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Set UTM params with priority: URL query params > partner defaults > existing cookies
  // For each param individually: URL value wins, then offer config, then leave cookie as-is
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const defaultParams = offer.defaultUtmParams || {};
    
    // For each UTM param, set value with priority: URL > offer config > existing cookie
    UTM_PARAMS.forEach(param => {
      const urlValue = urlParams.get(param);
      const configValue = defaultParams[param as keyof typeof defaultParams];
      
      if (urlValue) {
        // URL value takes highest priority
        setCookie(param, urlValue, COOKIE_EXPIRES_IN.maxAge);
      } else if (configValue) {
        // Offer config value is second priority
        setCookie(param, configValue, COOKIE_EXPIRES_IN.maxAge);
      }
      // Otherwise, leave existing cookie as-is
    });
    
    console.log('UTM params set - URL:', Object.fromEntries(urlParams), 'Config:', defaultParams);
  }, [offer.defaultUtmParams]);

  // Checkout page handles UTM params - it reads them from cookies
  // (cookies are set by gatsby-browser.js on every page load)
  const checkoutUrl = `/tracker/checkout?offer=${offer.type}`;
  const currentPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/tracker';
  const signInReturnPath = offer.hasOffer ? checkoutUrl : currentPath;
  const signInUrl = buildShopifyAccountUrl('/account/login', signInReturnPath);
  const registerUrl = buildShopifyAccountUrl('/account/register', checkoutUrl);

  // Handle CTA click (Claim offer / Claim free trial buttons)
  // All users go through web checkout flow (not App Store)
  const handleCtaClick = () => {
    if (isLoggedIn) {
      // Logged in - go straight to checkout
      window.location.href = checkoutUrl;
    } else {
      // Not logged in - show auth modal
      setShowAuthModal(true);
    }
  };

  // Handle scroll for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Feature list items with icons and videos
  const featureItems = [
    {
      icon: '/images/tracker/camera-icon.svg',
      title: 'Scan your plate',
      description: 'Snap a photo and our AI instantly recognises your food, calculating calories and macros in under 3 seconds.',
      video: '/videos/tracker/App_Feature_Video-Scan_Your_Plate.mp4',
    },
    {
      icon: '/images/tracker/microphone-icon.svg',
      title: 'Use voice notes',
      description: "Simply speak what you've eaten and the app logs it automatically – perfect for when you're on the go.",
      video: '/videos/tracker/App_Feature_Video-Use_Voice_Notes.mp4',
    },
    {
      icon: '/images/tracker/bell-icon.svg',
      title: 'Build the habit',
      description: 'Get customised reminders throughout the day so you never miss logging a meal and stay consistent with your goals.',
      video: '/videos/tracker/App_Feature_Video-Build_The_Habit.mp4',
    },
    {
      icon: '/images/tracker/target-icon.svg',
      title: 'Personalised targets',
      description: 'Get calorie and macro targets designed by nutritionists and tailored to your age, weight, activity level, and goals.',
      video: '/videos/tracker/App_Feature_Video-Personalised_Targets.mp4',
    },
    {
      icon: '/images/tracker/up-arrow-trend-icon.svg',
      title: 'Track your progress',
      description: "Log your weight over time to see clear trends and understand what's working for your body.",
      video: '/videos/tracker/App_Feature_Video-Track_Your_Progress.mp4',
    },
  ];

  // Feature video state management
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [isPausedByUser, setIsPausedByUser] = useState(false);
  const featureVideoRef = useRef<HTMLVideoElement>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoAdvanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle video ended - wait 3 seconds then advance to next feature
  const handleFeatureVideoEnded = useCallback(() => {
    if (isPausedByUser) return;

    autoAdvanceTimeoutRef.current = setTimeout(() => {
      setActiveFeatureIndex((prevIndex) => (prevIndex + 1) % featureItems.length);
    }, 3000);
  }, [isPausedByUser, featureItems.length]);

  // Handle manual feature selection
  const handleFeatureClick = (index: number) => {
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);

    setActiveFeatureIndex(index);
    setIsPausedByUser(true);

    pauseTimeoutRef.current = setTimeout(() => {
      setIsPausedByUser(false);
    }, 10000);
  };

  // Play video when active feature changes
  useEffect(() => {
    if (featureVideoRef.current) {
      featureVideoRef.current.load();
      featureVideoRef.current.play().catch(() => {});
    }
  }, [activeFeatureIndex]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    };
  }, []);

  // Current time for status bar
  const [currentTime, setCurrentTime] = useState('');
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Ref for horizontal scroll on mobile
  const featuresScrollRef = useRef<HTMLDivElement>(null);

  // Scroll to active feature on mobile
  useEffect(() => {
    if (featuresScrollRef.current && typeof window !== 'undefined' && window.innerWidth < 992) {
      const container = featuresScrollRef.current;
      const activeItem = container.children[activeFeatureIndex] as HTMLElement;
      if (activeItem) {
        const scrollLeft =
          activeItem.offsetLeft - (container.offsetWidth - activeItem.offsetWidth) / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeFeatureIndex]);

  // Built for real results items
  const resultsItems = [
    {
      icon: '/images/tracker/personalised-icon.svg',
      title: 'Personalised for you',
      description: "Tailored to your lifestyle, exercise routine, movement and goals. Get a plan that works for you. Not a one-size-fits-all approach.",
    },
    {
      icon: '/images/tracker/sparkles-icon.svg',
      title: 'Build healthy habits that stick',
      description: "Effortless 2 second meal tracking by photo, scan, voice, and typing. Adjust effortlessly with AI-powered insights that make healthy eating second nature.",
    },
    {
      icon: '/images/tracker/brain-icon.svg',
      title: 'Get expert guidance',
      description: "Personalised weekly coaching and guidance from leading nutritionists based on your diet. Science-backed advice that adapts to your needs.",
    },
  ];

  // User testimonials - App Store reviews
  const testimonials = [
    {
      title: 'Has helped me stay consistent',
      text:
        "This app has been a life saver. It's super easy to use and saves me lots of time with the barcode scanning feature! I've actually managed to stay consistent with my weight loss goals now",
      author: 'Ali2023G',
    },
    {
      title: 'Game Changer!',
      text:
        "This app is an absolute game changer for me. It's so easy to use and the photo feature saves me so much time searching for all the ingredients in my meals which used to take me forever!! Thank you 🤩",
      author: 'lil_mm',
    },
    {
      title: 'Really accurate!',
      text: "So easy to use and the results seem super accurate. Nice to be able to scan or type in your foods!",
      author: 'JMIM1996',
    },
  ];

  // Premium features
  const premiumFeatures = [
    {
      icon: '/images/tracker/camera-icon.svg',
      title: 'Photo Meal Scanning',
      description: 'Snap any food, get instant nutrition.',
    },
    {
      icon: '/images/tracker/barcode-icon.svg',
      title: 'Barcode Scanner',
      description: 'Scan packages for instant data.',
    },
    {
      icon: '/images/tracker/bar-chart-icon.svg',
      title: 'Advanced Macro Tracking',
      description: 'Detailed protein, carbs, and fat insights.',
    },
  ];

  const seoTitle = offer.hasOffer
    ? offer.subscriptionPeriod
      ? `Vitl Tracker - ${offer.subscriptionPeriod} for ${offer.annualPrice}`
      : `Vitl Tracker - ${offer.discountPercent} Off Annual Plan`
    : "Vitl Tracker - The World's Fastest Nutrition Tracking App";

  const seoDescription = offer.hasOffer
    ? offer.subscriptionPeriod
      ? `Get ${offer.subscriptionPeriod} for just ${offer.annualPrice} on Vitl Tracker - the world's fastest nutrition tracking app. AI-powered calorie and macro tracking.`
      : `Get ${offer.discountPercent} off the annual plan for Vitl Tracker - the world's fastest nutrition tracking app. AI-powered calorie and macro tracking.`
    : "Achieve your weight goals with the world's fastest nutrition tracking app. Start your free trial today.";

  return (
    <Layout showFooter={false} showHeader={false}>
      <SEO title={seoTitle} description={seoDescription} noindex={offer.hasOffer} />

      {/* Navigation */}
      <nav className={`${styles.nav} ${isScrolled ? styles.navScrolled : ''}`}>
        <div className={styles.navContainer}>
          {!offer.hasOffer ? (
            <Link to="/" className={styles.logo}>
              <img
                src="/images/tracker/vitl-tracker-logo-hero.png"
                alt="Vitl Tracker"
                className={styles.navLogoImage}
              />
            </Link>
          ) : (
            <img
              src="/images/tracker/vitl-tracker-logo-hero.png"
              alt="Vitl Tracker"
              className={styles.navLogoImage}
            />
          )}
          <div className={styles.navLinks}>
            {!offer.hasOffer && (
              <>
                <a href="#features" className={styles.navLink}>
                  Key Features
                </a>
                <a href="#reviews" className={styles.navLink}>
                  Reviews
                </a>
              </>
            )}
            {isLoggedIn && user ? (
              offer.hasOffer ? (
                <span className={styles.navUserName}>{user.firstName}</span>
              ) : (
                <Link to="/account/dashboard" className={styles.navUserName}>
                  {user.firstName}
                </Link>
              )
            ) : (
              <a href={signInUrl} className={styles.navLinkSignIn}>
                Sign In
              </a>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>{offer.appName || 'Vitl AI Calorie Tracker'}</h1>
            <div className={styles.heroTitleSubtitle}>
              Achieve your weight goals with the world's fastest nutrition tracking app
            </div>
            {offer.promoBadgeText && (
              <div className={styles.promoBadge}>
                {offer.partner?.logo ? (
                  <img
                    src={offer.partner?.logo}
                    alt={offer.partner?.name}
                    className={styles.partnerLogo}
                    onError={e => {
                      // Fallback to text if logo fails to load
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span className={styles.promoBadgeText}>{offer.promoBadgeText}</span>
                )}
                {offer.partner?.logo && (
                  <span className={styles.promoBadgeText}>{offer.promoBadgeText}</span>
                )}
              </div>
            )}
            <div className={styles.heroCtas}>
              <button className={styles.ctaButtonTrial} onClick={handleCtaClick}>
                {offer.ctaButtonText}
              </button>
            </div>
            <img
              src="/images/tracker/only-on-the-App-Store.svg"
              alt="Only on the App Store"
              className={styles.heroAppStoreBadge}
            />
            <div className={styles.heroTrust}>
              <p className={styles.heroTrusted}>
                From the UK's most loved personal nutrition company. Trusted by 1.5m users.
              </p>
              <div className={styles.heroTrustpilot}>
                <TrustPilot type={'MicroStar'} />
              </div>
            </div>
          </div>
          <div className={styles.heroMedia}>
            <video className={styles.heroVideo} autoPlay muted loop playsInline>
              <source src="/videos/tracker/tracker-hero-animation-yellow.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Features Section with Phone */}
      <section className={styles.featuresSection} id="features">
        <h2 className={styles.sectionTitle}>Powerful features</h2>
        <p className={styles.sectionSubtitle}>Our AI does the hard work so you don't have to.</p>
        <hr className={styles.sectionDivider} />
        <div className={styles.featuresContainer}>
          <div className={styles.featuresPhone}>
            <div className={styles.phoneFrame}>
              {/* Side buttons */}
              <div className={styles.phoneSideButtonLeftAction} />
              <div className={styles.phoneSideButtonLeftVolumeUp} />
              <div className={styles.phoneSideButtonLeftVolumeDown} />
              <div className={styles.phoneSideButtonRightPower} />

              {/* Status bar overlay */}
              <div className={styles.phoneStatusBar}>
                <span className={styles.statusTime}>{currentTime}</span>
                <div className={styles.statusNotch} />
                <div className={styles.statusIcons}>
                  <svg className={styles.statusSignal} viewBox="0 0 17 10" fill="currentColor">
                    <rect x="0" y="6" width="3" height="4" rx="0.5" />
                    <rect x="4.5" y="4" width="3" height="6" rx="0.5" />
                    <rect x="9" y="2" width="3" height="8" rx="0.5" />
                    <rect x="13.5" y="0" width="3" height="10" rx="0.5" />
                  </svg>
                  <svg className={styles.statusWifi} viewBox="0 0 15 11" fill="currentColor">
                    <path d="M7.5 9.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
                    <path d="M4.5 7.5a4.24 4.24 0 016 0l1-1a5.66 5.66 0 00-8 0l1 1z" />
                    <path d="M2 5a7.07 7.07 0 0111 0l1-1a8.49 8.49 0 00-13 0l1 1z" />
                  </svg>
                  <svg className={styles.statusBattery} viewBox="0 0 25 12" fill="currentColor">
                    <rect
                      x="0"
                      y="0"
                      width="22"
                      height="12"
                      rx="3"
                      stroke="currentColor"
                      strokeWidth="1"
                      fill="none"
                    />
                    <rect x="2" y="2" width="18" height="8" rx="1" />
                    <path d="M23 4v4a2 2 0 000-4z" />
                  </svg>
                </div>
              </div>

              <video
                ref={featureVideoRef}
                className={styles.featureVideo}
                muted
                playsInline
                onEnded={handleFeatureVideoEnded}
              >
                <source src={featureItems[activeFeatureIndex].video} type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Desktop: vertical list */}
          <div className={styles.featuresListDesktop}>
            {featureItems.map((item, index) => (
              <div
                key={index}
                className={`${styles.featureItem} ${
                  activeFeatureIndex === index ? styles.featureItemActive : ''
                }`}
                onClick={() => handleFeatureClick(index)}
              >
                <div className={styles.featureIcon}>
                  <img src={item.icon} alt="" className={styles.featureIconImage} />
                </div>
                <div className={styles.featureContent}>
                  <h4 className={styles.featureTitle}>{item.title}</h4>
                  <p className={styles.featureDescription}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: horizontal scroller */}
          <div className={styles.featuresScrollerMobile} ref={featuresScrollRef}>
            {featureItems.map((item, index) => (
              <div
                key={index}
                className={`${styles.featureCardMobile} ${
                  activeFeatureIndex === index ? styles.featureCardMobileActive : ''
                }`}
                onClick={() => handleFeatureClick(index)}
              >
                <div className={styles.featureCardHeader}>
                  <div className={styles.featureIcon}>
                    <img src={item.icon} alt="" className={styles.featureIconImage} />
                  </div>
                  <h4 className={styles.featureTitle}>{item.title}</h4>
                </div>
                <p className={styles.featureDescription}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Real Results Section */}
      <section className={styles.resultsSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Built for Real Results</h2>
          <p className={styles.sectionSubtitle}>
            Transform your nutrition and feel your best every day.
          </p>
          <hr className={styles.sectionDivider} />

          <div className={styles.resultsGrid}>
            {resultsItems.map((item, index) => (
              <div key={index} className={styles.resultsCard}>
                <div className={styles.resultsIcon}>
                  <img src={item.icon} alt="" className={styles.resultsIconImage} />
                </div>
                <h3 className={styles.resultsTitle}>{item.title}</h3>
                <p className={styles.resultsDescription}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className={styles.testimonialsSection} id="reviews">
        <div className={styles.testimonialsInner}>
          <h2 className={styles.sectionTitle}>What our users say</h2>
          <p className={styles.sectionSubtitle}>
            Join thousands of people using Vitl Tracker to reach their goals.
          </p>
          <hr className={styles.sectionDivider} />
          <div className={styles.testimonialsGrid}>
            {testimonials.map((testimonial, index) => (
              <div key={index} className={styles.testimonialCard}>
                <div className={styles.testimonialStars}>★★★★★</div>
                <h4 className={styles.testimonialTitle}>{testimonial.title}</h4>
                <p className={styles.testimonialText}>"{testimonial.text}"</p>
                <p className={styles.testimonialAuthor}>{testimonial.author}</p>
              </div>
            ))}
          </div>

          <TrustPilot type={'MicroStar'} />
        </div>
      </section>

      {/* Premium Features Section */}
      <section className={styles.premiumSection} id="download">
        <div className={styles.sectionContainer}>
          <div className={styles.premiumHeader}>
            <span className={styles.premiumIcon}>🎁</span>
            <h2 className={styles.premiumTitle}>
              {offer.hasTrial ? 'Try Premium Free' : 'Get Premium'}
            </h2>
            <p className={styles.premiumSubtitle}>
              {offer.hasTrial
                ? '3-day free trial. Cancel anytime.'
                : 'Unlock all premium features.'}
            </p>
          </div>
          <hr className={`${styles.sectionDivider} ${styles.dark}`} />

          <div className={styles.premiumCard}>
            <div className={styles.premiumCardHeader}>
              <img
                src="/images/tracker/crown-icon.svg"
                alt=""
                className={styles.premiumBadgeIcon}
              />
              <h3>Premium Features</h3>
              <p>Everything you need for advanced diet tracking</p>
            </div>

            <div className={styles.premiumFeaturesGrid}>
              {premiumFeatures.map((feature, index) => (
                <div key={index} className={styles.premiumFeature}>
                  <div className={styles.premiumFeatureIcon}>
                    <img src={feature.icon} alt="" className={styles.premiumFeatureIconImage} />
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Plan options */}
            <div className={styles.planOptionsContainer}>
              {/* Show custom subscription period for partner offers, otherwise show monthly + annual */}
              {offer.subscriptionPeriod ? (
                // Partner offer with custom subscription period (e.g., 3 months for £9.99)
                <div
                  className={`${styles.planOption} ${styles.planOptionRecommended} ${styles.planOptionSingle}`}
                >
                  <div className={styles.planBadge}>Save {offer.discountPercent}</div>
                  <div className={styles.planHeader}>
                    <h4 className={styles.planName}>{offer.subscriptionPeriod}</h4>
                    <div className={styles.planPrice}>
                      <span className={styles.planPriceAmount}>{offer.annualPrice}</span>
                    </div>
                  </div>
                  <p className={styles.planDescription}>
                    <span className={styles.planSavings}>{offer.discountPercent} off</span> vs
                    monthly
                  </p>
                  <button
                    className={`${styles.planButton} ${styles.planButtonPrimary}`}
                    onClick={handleCtaClick}
                  >
                    {offer.ctaButtonText}
                  </button>
                </div>
              ) : (
                <>
                  {/* Monthly plan */}
                  <div className={styles.planOption}>
                    <div className={styles.planHeader}>
                      <h4 className={styles.planName}>Monthly</h4>
                      <div className={styles.planPrice}>
                        <span className={styles.planPriceAmount}>{offer.monthlyPrice}</span>
                        <span className={styles.planPricePeriod}>/month</span>
                      </div>
                    </div>
                    <p className={styles.planDescription}>Flexible monthly billing</p>
                  </div>

                  {/* Annual plan - recommended */}
                  <div className={`${styles.planOption} ${styles.planOptionRecommended}`}>
                    <div className={styles.planBadge}>Save {offer.discountPercent}</div>
                    <div className={styles.planHeader}>
                      <h4 className={styles.planName}>Annual</h4>
                      <div className={styles.planPrice}>
                        <span className={styles.planPriceAmount}>{offer.annualPrice}</span>
                        <span className={styles.planPricePeriod}>/year</span>
                      </div>
                    </div>
                    <p className={styles.planDescription}>
                      <span className={styles.planSavings}>{offer.discountPercent} off</span> vs
                      monthly
                    </p>
                    <button
                      className={`${styles.planButton} ${styles.planButtonPrimary}`}
                      onClick={handleCtaClick}
                    >
                      {offer.ctaButtonText}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className={styles.premiumGuarantees}>
              <span className={styles.guaranteeItem}>
                <svg
                  className={styles.checkIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Cancel Anytime
              </span>
              <span className={styles.guaranteeItem}>
                <svg
                  className={styles.checkIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                No Hidden Fees
              </span>
              <span className={styles.guaranteeItem}>
                <svg
                  className={styles.checkIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Instant Access
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`${styles.footer} ${offer.hasOffer ? styles.footerOffer : ''}`}>
        <div className={styles.footerContainer}>
          <div className={styles.footerTop}>
            <div
              className={`${styles.footerBrand} ${
                offer.hasOffer ? styles.footerBrandFullWidth : ''
              }`}
            >
              <img src="/images/logo.svg" alt="Vitl" className={styles.footerLogoImage} />
              <p className={styles.footerTagline}>
                Log your meals in seconds with our AI-powered diet tracking app to reach your goals.
                Snap a photo, scan or type to track your diet and get smart calorie and nutritional
                insights, instantly!
              </p>
            </div>

            {!offer.hasOffer && (
              <div className={styles.footerLinks}>
                <div className={styles.footerColumn}>
                  <h4>Product</h4>
                  <a href="#features">Features</a>
                  <a href="#download">Premium</a>
                  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                    Download
                  </a>
                  <a href="mailto:hello@vitl.com">Support</a>
                </div>
                <div className={styles.footerColumn}>
                  <h4>Company</h4>
                  <Link to="/about">About Vitl</Link>
                  <Link to="/privacy">Privacy Policy</Link>
                  <Link to="/terms">Terms of Service</Link>
                  <a href="mailto:hello@vitl.com">Contact</a>
                </div>
              </div>
            )}
          </div>

          <div className={styles.footerDisclaimer}>
            <p className={styles.disclaimerTitle}>Disclaimer</p>
            <p className={styles.disclaimerText}>
              Vitl Tracker does not provide medical advice. Nutritional information is for general
              reference only. Always consult with a healthcare professional before making
              significant dietary changes.
            </p>
            {offer.privacyNotice && (
              <p className={styles.privacyNoticeText}>
                {offer.privacyNotice} <Link to="/privacy">Privacy Policy</Link>.
              </p>
            )}
          </div>

          <div className={styles.footerBottom}>
            <p>
              © {new Date().getFullYear()} Vitl. All rights reserved. Vitl Tracker is a trademark of
              Vitl.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal - for all users to go through web checkout */}
      <CSSTransition
        in={showAuthModal}
        timeout={300}
        classNames={{
          ...fadeTransition,
        }}
        unmountOnExit
      >
        <Modal type="-nutrients" handleClose={() => setShowAuthModal(false)}>
          <ModalBody type="-nutrients">
            <div className={styles.authModalTwoCol}>
              {/* Left panel - selling points (yellow) */}
              <div className={styles.authModalSell}>
                <img
                  src="/images/tracker/vitl-tracker-logo-hero.png"
                  alt="Vitl Tracker"
                  className={styles.authModalSellLogo}
                />
                <h3 className={styles.authModalSellTitle}>Claim your Vitl Tracker offer</h3>
                <p className={styles.authModalSellSubtitle}>Available with a free Vitl account</p>
                <ul className={styles.authModalSellPoints}>
                  <li>The world's fastest calorie tracker</li>
                  <li>From the No 1 personalised nutrition brand</li>
                  <li>
                    Join <strong>1.5m users</strong> who trust Vitl
                  </li>
                </ul>
              </div>

              {/* Right panel - auth */}
              <div className={styles.authModalAction}>
                {/* Steps at top */}
                <div className={styles.authModalStepsTop}>
                  <div
                    className={`${styles.authModalStepTop} ${
                      !isLoggedIn ? styles.authModalStepTopActive : styles.authModalStepTopCompleted
                    }`}
                  >
                    <span className={styles.authModalStepTopNumber}>{isLoggedIn ? '✓' : '1'}</span>
                    <span className={styles.authModalStepTopLabel}>Sign in</span>
                  </div>
                  <div className={styles.authModalStepTopDivider} />
                  <div
                    className={`${styles.authModalStepTop} ${
                      isLoggedIn ? styles.authModalStepTopActive : ''
                    }`}
                  >
                    <span className={styles.authModalStepTopNumber}>2</span>
                    <span className={styles.authModalStepTopLabel}>Claim offer</span>
                  </div>
                  <div className={styles.authModalStepTopDivider} />
                  <div className={styles.authModalStepTop}>
                    <span className={styles.authModalStepTopNumber}>3</span>
                    <span className={styles.authModalStepTopLabel}>Download</span>
                  </div>
                </div>

                {/* Buttons */}
                {isLoggedIn ? (
                  <div className={styles.authModalButtons}>
                    <a href={checkoutUrl} className={styles.authModalButtonVitlPrimary}>
                      Continue to checkout
                    </a>
                    <button
                      onClick={() => setShowAuthModal(false)}
                      className={styles.authModalButtonVitlSecondary}
                    >
                      Go back
                    </button>
                  </div>
                ) : (
                  <div className={styles.authModalButtons}>
                    <a href={registerUrl} className={styles.authModalButtonVitlPrimary}>
                      Create free account
                    </a>

                    <div className={styles.authModalDividerSection}>
                      <hr className={styles.authModalHr} />
                      <span className={styles.authModalDividerText}>
                        Already have an account?{' '}
                        <a href={signInUrl} className={styles.authModalSignInLink}>
                          Sign in
                        </a>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
        </Modal>
      </CSSTransition>
    </Layout>
  );
};

export default TrackerLanding;
export { APP_STORE_URL };
